import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { environment } from "../environments/environment";

export interface FacilityCheckoutItem {
  pricing_tier_id: number;
  user_preferred_facility_id?: number;
  ccn_number?: string;
  facility_name?: string;
  facility_type?: string | number;
  facility_type_id?: number;
  city?: string;
  state?: string;
  country?: string;
  phone_number?: string;
  address_1?: string;
  address_2?: string;
  postal_code?: string;
  vertical?: string;
  primary_profile_slug?: string;
  profile_targeting_json?: unknown;
  primary_taxonomy_code?: string;
  primary_taxonomy_desc?: string;
  skip_npi_validation?: boolean;
  skip_ccn_validation?: boolean;
}

export interface CreateCheckoutSessionPayload {
  metadata: {
    user_id: string;
    ccn_number?: string;
    facility_name?: string;
    city?: string;
    state?: string;
    country?: string;
    address_1?: string;
    address_2?: string;
    postal_code?: string;
    facility_type?: number | string;
    facility_type_id?: number;
    vertical?: string;
    primary_profile_slug?: string;
    profile_targeting_json?: unknown;
    phone_number?: string;
    username?: string;
    user_type?: string;
    skip_npi_validation?: boolean;
    bypass_checkout?: boolean; // if true, backend treats as bypass (no redirect needed)
    discount_coupon?: string; // Applied discount coupon code for Stripe checkout
    user_preferred_facility_id?: number; // ID from user-preferred-facilities record
    facility_id?: number; // optional; facility plans use it, add-ons do not require it
    is_addon?: boolean; // company-scoped add-on purchase (skip facility enrollment)
  };
  pricing_tier_id: number;
  /** Multi-facility cart — one customer_plan per item; one invoice for quantity=N */
  facilities?: FacilityCheckoutItem[];
  //pricing_tier_price_type: "monthly" | "annual";
  success_url: string;
  cancel_url: string;
}

export interface CreateCheckoutSessionResult {
  session_id?: string;
  checkout_url?: string;
  /** Rules DB facility id when the server created the facility (bypass checkout). */
  facility_id?: number;
  association_id?: number;
}

export interface CreateCheckoutSessionResponse {
  success?: boolean;
  message?: string;
  status_code?: number;
  result?: CreateCheckoutSessionResult;
}

const BASE_URL = getApiOrigin();

export interface DiscountCodeItem {
  id: number;
  name: string;
  type: string;

  // Values used for pricing calculations
  discount_percentage?: number; // e.g. for `percent` discounts
  discount_price?: number; // e.g. for `amount` discounts
  max_discount_price?: number;

  // Metadata from MCM
  description?: string;
  sku_id?: number;
  region_id?: number;
  region_name?: string;

  is_active?: number;
  is_delete?: number;

  start_date?: string;
  end_date?: string;

  created_by?: number;
  created_date?: string;
  updated_by?: number;
  updated_date?: string;
  logs?: Record<string, unknown>;
}

export interface DiscountCodesResponse {
  codes: DiscountCodeItem[];
}

/** Fetch discount codes from API (same format as former discounts.json). */
export async function getDiscountCodes(
  discountType?: string,
  discountName?: string,
): Promise<DiscountCodesResponse> {
  const mcmBase = environment.masterConfigurationManagerURL || process.env.APP_MASTER_CONFIGURATION_MANAGER_URL;
  if (!mcmBase) {
    console.warn(
      "APP_MASTER_CONFIGURATION_MANAGER_URL is not set; cannot fetch discount codes.",
    );
    return { codes: [] };
  }

  const url = new URL("/discount_coupon", mcmBase);
  url.searchParams.set("page", "1");
  url.searchParams.set("page_size", "10000000");
  const filters: Record<string, unknown> = { is_active: 1 };
  if (discountType && discountType.trim()) {
    filters.type = discountType.trim();
  } else if (!(discountName && discountName.trim())) {
    filters.type = "bypass";
  }
  if (discountName && discountName.trim()) {
    filters.name = discountName.trim();
  }
  url.searchParams.set("filters", JSON.stringify(filters));

  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });
  const responseData = await response.json();
  if (!responseData.success) {
    const text = await response.text().catch(() => "");
    console.warn(`Failed to fetch discount codes (${response.status}) ${text}`);
    return { codes: [] };
  }
  const resultData = (responseData as any)?.result?.data ?? (responseData as any)?.data;
  if (Array.isArray(resultData)) {
    return { codes: resultData as DiscountCodeItem[] };
  }
  if (Array.isArray((resultData as any)?.codes)) {
    return resultData as DiscountCodesResponse;
  }
  // Fallback: treat unknown shapes as empty list
  return { codes: [] };
}

export interface PlanDetails {
  features: {
    setup?: string;
    subscription?: string;
    contract_term?: string;
    [key: string]: string | undefined;
  };
  plan_data: string[];
}

export interface ContentPackageDetail {
  id: number;
  name: string;
  unit: string;
  price: number;
  description: string;
  feature_count: number;
  sku_feature_id: number;
  sku_feature_name: string;
}

export interface PricingTier {
  id: number;
  name: string;
  frequency_id: number;
  frequency_name: string;
  region_id: number;
  price: number;
  region_name: string;
  plan_details: PlanDetails[];
  description: string;
  period: string | null;
  region_currency_symbol: string;
  is_default: boolean;
  is_popular: boolean;
  is_free: boolean;
  redirect_to: string | null;
  display_order: number | null;
  is_active: number;
  is_delete: number;
  created_date: string;
  updated_date: string;
  created_by: number;
  updated_by: number;
  logs: Record<string, any>;
  content_package_details: ContentPackageDetail[];
  
  // Legacy fields for backward compatibility
  tier_name?: string;
  display_name?: string;
  frequency?: "monthly" | "yearly";
  features?: {
    basic_search: boolean;
    advanced_search?: boolean;
    template_downloads?: boolean;
    ai_agents?: boolean;
    assessments?: boolean;
    analytics?: boolean;
    data_access: string;
    support: string;
    storage_limit_gb: number;
    api_calls_per_month: number;
  };
  max_users?: number;
  max_searches_per_month?: number;
  allows_downloads?: boolean;
  allows_agents?: boolean;
  allows_assessments?: boolean;
  display_features?: Array<{ name: string }>;
  currency?: string;
  formatted_features?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface PricingTiersResponse {
  success: boolean;
  data: PricingTier[];
  message: string | null;
}

export interface AnnualPlanComparisonResponse {
  success?: boolean;
  data?: {
    savings_percentage?: number;
    discount_percentage?: number;
    percentage?: number;
    [key: string]: unknown;
  };
  result?: {
    savings_percentage?: number;
    discount_percentage?: number;
    percentage?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface CreateUserPreferredPlanPayload {
  user_id: number;
  plan_tier_id: number;
}

export interface CreateUserPreferredPlanResponse {
  status: number;
  message: string;
  data: {
    id: number;
    user_id: number;
    plan_tier_id: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

export interface CheckUserFacilityAssociationPayload {
  /** Always the NPI. Maps to facilities.facility_unique_id. */
  ccn_number: string;
  /** Optional CMS CCN. Saved on the same row, not used as the unique id. */
  medicare_ccn?: string;
  facility_name: string;
  city: string;
  state: string;
  country: string;
  facility_type: string;
}

export interface CheckUserFacilityAssociationResponse {
  success: boolean;
  message?: string;
  status_code?: number;
  data?: {
    can_associate: boolean;
    reason?: string | null;
    error_code?: string;
    is_already_associated?: boolean;
    association_id?: number | null;
    facility_id?: number;
  };
}

export interface CreateUserPreferredFacilityPayload {
  user_id: number;
  facility_details: UserPreferredFacilityDetails;
  is_verified: boolean;
}

export interface UserPreferredFacilityDetails {
  user_id: string;
  ccn_number?: string;
  facility_name?: string;
  facility_type?: string;
  facility_type_id?: number;
  city?: string;
  state?: string;
  country?: string;
  address_1?: string;
  address_2?: string;
  postal_code?: string;
  phone_number?: string;
  skip_npi_validation?: boolean;
  username?: string;
  pricing_tier_id: number;
  bypass_checkout: boolean;
  discount_coupon: string;
  // Vertical slug for non-healthcare add-facility flows (e.g. "food_service",
  // "aviation"). When set, the Rules server stamps Facility.vertical
  // with this value so the Hub's vertical-filtered Selected-Facility dropdown
  // can show the new facility for the active preview vertical. Optional and
  // omitted on legacy healthcare flows so the server falls back to its
  // FacilityType.vertical → "healthcare" default.
  vertical?: string;
  primary_profile_slug?: string;
  profile_targeting_json?: unknown;
  aviation_targeting?: unknown;
  activity_domains?: string[];
  operation_contexts?: string[];
  asset_types?: string[];
  certificates_or_approvals?: string[];
  regulation_packs?: string[];
  overlay_packs?: string[];
  penalty_packs?: string[];
  /** NUCC primary taxonomy from NPI/registry (persisted on Facility when checkout creates the row). */
  primary_taxonomy_code?: string;
  primary_taxonomy_desc?: string;
}

export interface CreateUserPreferredFacilityResponse {
  success: boolean;
  message: string;
  data?: {
    id: number;
    user_id: number;
    facility_id: number;
    is_verified: boolean;
    is_active: boolean;
    is_delete: boolean;
    created_at: string;
    updated_at: string;
  };
}

export interface UserPreferredFacility {
  id: number;
  user_id: number;
  facility_details?: UserPreferredFacilityDetails | null;
  status: string;
  is_verified: boolean;
  is_active: boolean;
  is_delete: boolean;
  created_at: string;
  updated_at: string;
  user_first_name?: string | null;
  user_last_name?: string | null;
  facility_name?: string | null;
  facility_type?: string | null;
  ccn_number?: string | null;
  facility_unique_id?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  phone_number?: string | null;
  discount_coupon_name?: string | null;
  discount_coupon_id?: number | null;
  discount_end_date?: string | null;
  promocode_status?: string | null;
}

export interface GetUserPreferredFacilitiesResponse {
  success: boolean;
  message?: string;
  result: {
    data: UserPreferredFacility[];
    total: number;
    page: number;
    page_size: number;
  };
}

export async function getPricingTiers(filters?: {
  /** MCM plan id. When set, fetch that plan only — no frequency/facility catalog filter. */
  id?: number;
  frequency?: "monthly" | "yearly";
  facility_type?: number;
  sku_name?: string;
  vertical?: string;
  primary_profile_slug?: string;
  ardcode?: string;
  /** ISO 4217, e.g. CAD for Canada — passed to MCM /plans filters when set */
  currency?: string;
  /** Lowercase region slug for MCM, e.g. canada — passed when set */
  country?: string;
}): Promise<PricingTiersResponse> {
  const byPlanId =
    typeof filters?.id === "number" && Number.isFinite(filters.id) && filters.id > 0;
  const frequency_name = filters?.frequency === "monthly" ? "monthly" : "yearly";

  // Build filters object for the API
  const filtersObj: Record<string, any> = {};

  if (byPlanId) {
    filtersObj.id = filters!.id;
  }
  
  // Add sku_id to filters
  if (typeof filters?.sku_name === "string") {
    filtersObj.sku_name = filters.sku_name;
  }
  
  // Catalog list needs frequency. Direct plan id must not AND a yearly filter.
  if (!byPlanId) {
    filtersObj.frequency = frequency_name;
  }

  if (
    typeof filters?.currency === "string" &&
    filters.currency.trim() !== ""
  ) {
    filtersObj.currency = filters.currency.trim().toUpperCase();
  }

  if (
    typeof filters?.country === "string" &&
    filters.country.trim() !== ""
  ) {
    filtersObj.country = filters.country.trim().toLowerCase();
  }

  let query = `?filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;

  if (!byPlanId && typeof filters?.facility_type === "number") {
    query += `&facility_type=${filters.facility_type}`;
  }

  if (
    !byPlanId &&
    typeof filters?.vertical === "string" &&
    filters.vertical.trim() !== ""
  ) {
    query += `&vertical=${encodeURIComponent(filters.vertical.trim())}`;
  }

  if (
    !byPlanId &&
    typeof filters?.primary_profile_slug === "string" &&
    filters.primary_profile_slug.trim() !== ""
  ) {
    query += `&primary_profile_slug=${encodeURIComponent(
      filters.primary_profile_slug.trim(),
    )}`;
  }

  if (typeof filters?.ardcode === "string" && filters.ardcode.trim() !== "") {
    query += `&ardcode=${encodeURIComponent(filters.ardcode)}`;
  }

  const token = getValidAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}/api/v1/pricing-tiers${query}`, {
    method: "GET",
    headers,
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch pricing tiers (${response.status}) ${text}`
    );
  }

  return (await response.json()) as PricingTiersResponse;
}

function omitPricingRegionalFilters(
  filters?: Parameters<typeof getPricingTiers>[0],
): Parameters<typeof getPricingTiers>[0] {
  if (!filters) return {};
  const { currency: _c, country: _n, ...rest } = filters;
  return rest;
}

/**
 * Load pricing tiers; if MCM rejects regional filters or returns no rows, retry once
 * without `currency` / `country` so US-default plans still load.
 */
export async function getPricingTiersWithRegionalFallback(
  filters?: Parameters<typeof getPricingTiers>[0],
): Promise<PricingTiersResponse> {
  const hasRegional =
    !!(filters?.currency && String(filters.currency).trim()) ||
    !!(filters?.country && String(filters.country).trim());

  const stripped = () => omitPricingRegionalFilters(filters);

  try {
    const r = await getPricingTiers(filters);
    if (!r.success) {
      if (hasRegional) return getPricingTiers(stripped());
      return r;
    }
    if (Array.isArray(r.data) && r.data.length > 0) return r;
    if (hasRegional) return getPricingTiers(stripped());
    return r;
  } catch (e) {
    if (hasRegional) return getPricingTiers(stripped());
    throw e;
  }
}

export async function getAnnualPlanComparison(
  planId: number,
  planType: "annual" | "monthly" = "annual",
  options?: {
    sku_name?: string;
    vertical?: string;
    primary_profile_slug?: string;
  },
): Promise<AnnualPlanComparisonResponse> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const queryParamKey =
    planType === "monthly" ? "monthly_plan_id" : "annual_plan_id";

  const params = new URLSearchParams({
    [queryParamKey]: String(planId),
  });
  const sku = options?.sku_name?.trim();
  if (sku) {
    params.set("sku_name", sku);
  }
  const vertical = options?.vertical?.trim();
  if (vertical) {
    params.set("vertical", vertical);
  }
  const profile = options?.primary_profile_slug?.trim();
  if (profile) {
    params.set("primary_profile_slug", profile);
  }

  const response = await fetch(
    `${BASE_URL}/api/v1/pricing-tiers/annual-plan-comparison?${params.toString()}`,
    {
      method: "GET",
      headers,
    },
  );

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch annual plan comparison (${response.status}) ${text}`,
    );
  }

  return (await response.json()) as AnnualPlanComparisonResponse;
}

export async function checkUserFacilityAssociation(
  payload: CheckUserFacilityAssociationPayload
): Promise<CheckUserFacilityAssociationResponse> {
  // Get access token from localStorage
  const token = getValidAccessToken();
  
  // Build headers with Authorization if token exists
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const response = await fetch(
    `${BASE_URL}/api/v1/facility-management/check-user-facility-association`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    }
  );

  check401(response);

  const responseData = await response.json();
  
  // Log the response for debugging
  console.log("API Response:", responseData);
  console.log("Response status:", response.status);
  
  // Even if response is OK, we still return it so the caller can check can_associate
  // Only throw error for actual HTTP errors (4xx, 5xx)
  if (!response.ok) {
    const text = JSON.stringify(responseData);
    throw new Error(
      `Failed to check user facility association (${response.status}) ${text}`
    );
  }
  
  return responseData as CheckUserFacilityAssociationResponse;
}

export interface NotifyUnsupportedFacilityTypePayload {
  taxonomy_code: string;
  /** NUCC / source-system display name when available (improves LLM enrichment). */
  taxonomy_display_name?: string;
  facility_type_label?: string;
  facility_name?: string;
  npi_number?: string;
  city?: string;
  state?: string;
  country?: string;
}

export interface NotifyUnsupportedFacilityTypeResponse {
  success?: boolean;
  message?: string;
  data?: {
    report_id?: number;
    queued?: boolean;
    email_pending?: boolean;
    notified?: boolean;
    recipients_attempted?: number;
    succeeded_count?: number;
    failed_count?: number;
    reason?: string;
  };
}

export async function notifyUnsupportedFacilityType(
  payload: NotifyUnsupportedFacilityTypePayload,
): Promise<NotifyUnsupportedFacilityTypeResponse> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await fetch(
    `${BASE_URL}/api/v1/facility-management/notify-unsupported-facility-type`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    },
  );

  check401(response);
  const responseData = (await response.json()) as NotifyUnsupportedFacilityTypeResponse;
  if (!response.ok) {
    const text = JSON.stringify(responseData);
    throw new Error(
      `Failed to notify unsupported facility type (${response.status}) ${text}`,
    );
  }
  return responseData;
}

export async function createUserPreferredFacility(
  payload: CreateUserPreferredFacilityPayload,
): Promise<CreateUserPreferredFacilityResponse> {
  const token = getValidAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE_URL}/api/v1/user-preferred-facilities`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    },
  );

  check401(response);

  const responseData = (await response.json()) as CreateUserPreferredFacilityResponse;

  // If response is not OK or success is false, throw error with the message
  if (!response.ok) {
    const errorMessage = responseData.message || 
      `Failed to create user preferred facility (${response.status})`;
    throw new Error(JSON.stringify({ message: errorMessage, success: false }));
  }

  // If response is OK but success is false, also throw error
  if (!responseData.success) {
    const errorMessage = responseData.message || 
      "Failed to create user preferred facility";
    throw new Error(JSON.stringify({ message: errorMessage, success: false }));
  }

  return responseData;
}

const _preferredFacilitiesInFlight = new Map<
  string,
  Promise<GetUserPreferredFacilitiesResponse>
>();

function _preferredFacilitiesFlightKey(
  page: number,
  pageSize: number,
  userId?: number,
  isActive?: boolean,
): string {
  return `${page}\0${pageSize}\0${userId ?? ""}\0${String(isActive ?? "")}`;
}

export function clearUserPreferredFacilitiesInflightForTests(): void {
  _preferredFacilitiesInFlight.clear();
}

export async function getUserPreferredFacilities(
  page = 1,
  pageSize = 20,
  userId?: number,
  isActive?: boolean,
): Promise<GetUserPreferredFacilitiesResponse> {
  const flightKey = _preferredFacilitiesFlightKey(page, pageSize, userId, isActive);
  const existing = _preferredFacilitiesInFlight.get(flightKey);
  if (existing) {
    return existing;
  }

  const token = getValidAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let url = `${BASE_URL}/api/v1/user-preferred-facilities?page=${encodeURIComponent(
    String(page),
  )}&page_size=${encodeURIComponent(String(pageSize))}`;

  // Build filters object
  const filters: Record<string, any> = {};
  if (typeof userId === "number") {
    filters.user_id = userId;
  }
  if (typeof isActive === "boolean") {
    filters.is_active = isActive;
  }

  // Add filters as JSON-encoded query parameter if any filters exist
  if (Object.keys(filters).length > 0) {
    url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
  }

  const execute = async (): Promise<GetUserPreferredFacilitiesResponse> => {
    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `Failed to fetch user preferred facilities (${response.status}) ${text}`,
      );
    }

    return (await response.json()) as GetUserPreferredFacilitiesResponse;
  };

  const promise = execute().finally(() => {
    _preferredFacilitiesInFlight.delete(flightKey);
  });
  _preferredFacilitiesInFlight.set(flightKey, promise);
  return promise;
}

export interface GetUserPreferredFacilityResponse {
  success: boolean;
  message: string;
  data: UserPreferredFacility;
}

export async function getUserPreferredFacilityById(
  facilityId: number,
): Promise<GetUserPreferredFacilityResponse> {
  const token = getValidAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const url = `${BASE_URL}/api/v1/user-preferred-facilities/${facilityId}`;

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch user preferred facility (${response.status}) ${text}`,
    );
  }

  return (await response.json()) as GetUserPreferredFacilityResponse;
}

/** Soft-delete a UPF row (cart remove / cap failure after create). */
export async function softDeleteUserPreferredFacility(
  facilityId: number,
): Promise<void> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(
    `${BASE_URL}/api/v1/user-preferred-facilities/${facilityId}`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify({ is_delete: true, is_active: false }),
    },
  );
  check401(response);
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to remove preferred facility (${response.status}) ${text}`,
    );
  }
}

export interface UpdatePaymentMethodOptions {
  returnUrl?: string;
  cancelUrl?: string;
}

/** Hash-router return URLs for MCM update payment method flow. */
export function getPaymentMethodUpdateReturnUrls(origin = window.location.origin) {
  return {
    returnUrl: `${origin}/#/payment-method-update-success`,
    cancelUrl: `${origin}/#/payment-method-update-failure`,
  };
}

export interface StartUpdatePaymentMethodResponse {
  success?: boolean;
  message?: string;
  result?: {
    checkout_url?: string;
    checkout_session_id?: string;
  };
}

/**
 * Authenticated POST to MCM that returns a Stripe Checkout URL for card update.
 * Hub navigates to checkout_url; do not open the MCM URL directly in the browser.
 */
export async function startUpdatePaymentMethod(
  customerId: string | number,
  options?: UpdatePaymentMethodOptions,
): Promise<void> {
  const mcmBase =
    environment.masterConfigurationManagerURL ||
    process.env.APP_MASTER_CONFIGURATION_MANAGER_URL;
  if (!mcmBase) {
    throw new Error(
      "APP_MASTER_CONFIGURATION_MANAGER_URL is not set; cannot update payment method.",
    );
  }

  const token = getValidAccessToken();
  if (!token) {
    throw new Error("Authentication required to update payment method.");
  }

  const defaults = getPaymentMethodUpdateReturnUrls();
  const returnUrl = options?.returnUrl ?? defaults.returnUrl;
  const cancelUrl = options?.cancelUrl ?? defaults.cancelUrl;

  const response = await fetch(
    new URL("/stripe/update_payment_method", mcmBase).toString(),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        customer_id: Number(customerId),
        return_url: returnUrl,
        cancel_url: cancelUrl,
      }),
    },
  );

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to start payment method update (${response.status}) ${text}`,
    );
  }

  const payload = (await response.json()) as StartUpdatePaymentMethodResponse;
  const checkoutUrl = payload?.result?.checkout_url;
  if (!checkoutUrl) {
    throw new Error(payload?.message || "Stripe Checkout URL was not returned");
  }
  window.location.assign(checkoutUrl);
}

export async function createCheckoutSession(
  payload: CreateCheckoutSessionPayload
): Promise<CreateCheckoutSessionResponse> {
  // Get access token from localStorage
  const token = getValidAccessToken();
  
  // Build headers with Authorization if token exists
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  //console.log("Payload:", JSON.stringify(payload, null, 2));
  const response = await fetch(
    `${BASE_URL}/api/v1/stripe/create-checkout-session`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    }
  );

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to create checkout session (${response.status}) ${text}`
    );
  }
  
  const responseData = await response.json() as CreateCheckoutSessionResponse;
  
  // Check success flag - if success is false, throw error with message
  if (responseData.success === false) {
    throw new Error("Unable to process payment. Please try again. If issue persists, contact us at support@affableone.ai");
  }
  
  console.log("Payload:", JSON.stringify(payload, null, 2));
  return responseData;
}

export const OIG_ADDON_BLOCKS = [
  {
    planId: 920001,
    checks: 200,
    priceUsd: 900,
    bestFit: "Small staff/vendor expansion",
  },
  {
    planId: 920002,
    checks: 500,
    priceUsd: 2000,
    bestFit: "Moderate provider/vendor screening",
  },
  {
    planId: 920003,
    checks: 1000,
    priceUsd: 3600,
    bestFit: "Higher-volume recurring screening",
  },
] as const;

export const OIG_INCLUDED_PER_FACILITY_COPY =
  "100 OIG checks/month included per facility.";

export const OIG_CUSTOM_BLOCK_MAILTO =
  "mailto:contact@unicomply.ai?subject=OIG%20check%20block%20over%201000";

export type OigAddonBlock = {
  planId: number;
  checks: number;
  priceUsd: number;
};

export function recommendOigAddonCombo(shortfall: number): OigAddonBlock[] {
  const need = Math.max(0, Math.floor(shortfall));
  if (need <= 0) {
    return [];
  }
  let best: { price: number; blocks: number; extra: number; n1000: number; n500: number; n200: number } | null =
    null;
  const max200 = Math.floor(need / 200) + 3;
  const max500 = Math.floor(need / 500) + 2;
  const max1000 = Math.floor(need / 1000) + 2;
  for (let n1000 = 0; n1000 <= max1000; n1000 += 1) {
    for (let n500 = 0; n500 <= max500; n500 += 1) {
      for (let n200 = 0; n200 <= max200; n200 += 1) {
        const total = n200 * 200 + n500 * 500 + n1000 * 1000;
        if (total < need) continue;
        const price = n200 * 900 + n500 * 2000 + n1000 * 3600;
        const blocks = n200 + n500 + n1000;
        const extra = -total;
        if (
          !best ||
          blocks < best.blocks ||
          (blocks === best.blocks && price < best.price) ||
          (blocks === best.blocks && price === best.price && extra < best.extra)
        ) {
          best = { price, blocks, extra, n1000, n500, n200 };
        }
      }
    }
  }
  if (!best) {
    return [{ planId: 920003, checks: 1000, priceUsd: 3600 }];
  }
  const combo: OigAddonBlock[] = [];
  for (let i = 0; i < best.n1000; i += 1) combo.push({ planId: 920003, checks: 1000, priceUsd: 3600 });
  for (let i = 0; i < best.n500; i += 1) combo.push({ planId: 920002, checks: 500, priceUsd: 2000 });
  for (let i = 0; i < best.n200; i += 1) combo.push({ planId: 920001, checks: 200, priceUsd: 900 });
  return combo;
}

export function formatOigQuotaIncreaseMessage(
  remaining: number,
  estimated: number,
  adminEmail?: string | null,
): string {
  const rem = Math.max(0, remaining);
  const shortfall = Math.max(0, estimated - rem);
  const combo = recommendOigAddonCombo(shortfall);
  const extra = combo.reduce((sum, block) => sum + block.checks, 0);
  const target = rem + extra;
  const contact = (adminEmail || "").trim() || "contact@unicomply.ai";
  return `Contact ${contact} for Exclusion quota increase from ${rem} to ${target}.`;
}

export async function createUserPreferredPlan(
  payload: CreateUserPreferredPlanPayload
): Promise<CreateUserPreferredPlanResponse> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}/api/v1/user-preferred-plans`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to create user preferred plan (${response.status}) ${text}`
    );
  }

  return (await response.json()) as CreateUserPreferredPlanResponse;
}

